import { defineComponent, onBeforeUnmount, onMounted, provide, ref, watch } from "vue";
import { addScopedGlobalListener, onRefreshedOrMounted, OptionalVueProp, runLoadActions, VueProp } from "../../core/vueHelpers";
import { FetchDataBehavior, SkyVueData, State } from "../core/store/types";
import { getContentElements } from "../core/utilities";
import { dashboardItemStore } from "../dashboardItem/store";
import { VueBrowseClasses } from "./classes";
import { BrowseComponents } from "./components";
import { browseStore, browseStoreKey } from "./store";

export default /*#__PURE__*/ defineComponent(
    {
        name: "VueBrowse",
        props:
        {
            skyVueData: VueProp<SkyVueData>(Object, {
                validator: (value) =>
                {
                    return value.id != null;
                }
            }),
            parent: OptionalVueProp<State<{}, {}, {}, {}>>(Object),
        },
        setup(props, context)
        {
            const state = browseStore.addState(props.skyVueData, props.parent);
            context.expose({ state });

            const widgetState = dashboardItemStore.injectDashboardItemState();

            provide(browseStoreKey, state);

            const root = ref();

            // Life Cycle Hooks
            onMounted(() =>
            {
                state.data.fetch(FetchDataBehavior.Replace);

                watch(() => state.data.postData, () =>
                {
                    state.data.isLoaded = false;
                    state.selectRow("");
                    state.data.fetch(FetchDataBehavior.Replace);
                },
                {
                    deep: true
                });

                if (widgetState)
                {
                    watch(() => widgetState.features.settings.isPreview, () =>
                    {
                        state.features.dataSource.isPreview = widgetState.features.settings.isPreview;
                    });
                }

                watch(() => state.data.values, () =>
                {
                    if (typeof state.rowsByID[state.selectedRowID] === "undefined")
                    {
                        if (state.features.settings.autoSelectRows && state.displayRows.length > 0)
                        {
                            state.selectRow(state.displayRows[0].rowID, true);
                        }
                        else
                        {
                            state.selectRow("");
                        }
                    }
                },
                {
                    deep: true
                });

                if (!state.features.settings.manuallyHandleKeyboardNavigation)
                {
                    addScopedGlobalListener(document, "keydown", moveSelectedRowHandler);
                }
            });
            onRefreshedOrMounted(state, () => runLoadActions(props.skyVueData?.htmlData["load-action"], root.value));

            onBeforeUnmount(() => 
            {
                browseStore.deleteState(state.browseID);
            });

            // Component Rendering
            return () => (
                <div
                    id={state.browseID}
                    ref={(ref) => root.value = ref}
                    class=
                    {{
                        [VueBrowseClasses.Container]: true,
                        [VueBrowseClasses.ContainerSelected]: state.isSelected,
                        [VueBrowseClasses.StaticHeight]: state.features.settings.isStaticHeight,
                        [VueBrowseClasses.DynamicHeight]: !state.features.settings.isStaticHeight,
                        [VueBrowseClasses.OverflowUnset]: !!state.features.settings.infiniteScrollBodyIDOverride || !!state.features.settings.infiniteScrollBodyClassOverride,
                    }}
                    onClick={() => { if (state.features.settings.canBeSelected) { state.isSelected = true; }}}
                    onFocusin={() => { if (state.features.settings.canBeSelected) { state.isSelected = true; } }}
                    onFocusout={() => { if (state.features.settings.deselectOnUnFocus && state.features.settings.canBeSelected) { state.isSelected = false; } }}
                >
                    {getContentElements(state, BrowseComponents)}
                </div >);

            function moveSelectedRowHandler(event: JQuery.KeyDownEvent): boolean
            {
                const eventKeys = window.skyward.keys.getKeyNamesFromEvent(event);
                const selectPreviousRow = eventKeys.ARROW_UP;
                const selectNextRow = eventKeys.ARROW_DOWN;
                const deltaMove = selectNextRow ? 1 : selectPreviousRow ? -1 : 0;

                if (deltaMove === 0 || !state.isSelected || window.skyward.browse.shouldIgnoreKeyboardEvent(document.activeElement))
                {
                    return true;
                }

                // Don't consider browses not in the current modal layer
                const modalLayers = window.skyward.modalLayer.getOpenModalLayers();
                if (modalLayers.length > 0)
                {
                    const browseModalLayer = window.skyward.modalLayer.getLayerFromElement(root.value);
                    if (browseModalLayer === undefined || !modalLayers.last().is(browseModalLayer.container))
                    {
                        event.preventDefault();
                        event.stopPropagation();
                        return false;
                    }
                }

                state.moveSelectedRow(deltaMove);

                event.preventDefault();
                event.stopPropagation();
                return false;
            }
        }
    }
);
